import { useCallback, useEffect, useRef } from "react";

import { useUiFlag } from "../../hooks/useUiFlag";
import { runStoryTour } from "./storyTour";

export interface UseStoryBuilderTourOptions {
  /**
   * True once the builder has rendered a scene — the tour's anchors don't
   * exist before that, and auto-starting against a loading screen would burn
   * the one first-run impression on an empty page.
   */
  ready: boolean;
}

export interface UseStoryBuilderTourResult {
  /** Replay on demand (the header's ? button). */
  start: () => void;
  /** True before the first run has been offered — used to badge the ? button. */
  neverRun: boolean;
}

/**
 * Auto-runs the builder coach tour on a user's first visit, and exposes a
 * manual replay.
 *
 * The "seen it" marker is a server-side ui_flag, so it follows the person
 * across browsers rather than re-firing on every new machine. Skipping counts
 * as seen — someone who dismissed it does not want it again tomorrow.
 */
export function useStoryBuilderTour({
  ready,
}: UseStoryBuilderTourOptions): UseStoryBuilderTourResult {
  const { value, loading, set } = useUiFlag("storyBuilderTour");
  const startedRef = useRef(false);
  const stopRef = useRef<(() => void) | null>(null);

  const start = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    stopRef.current = runStoryTour((outcome) => {
      stopRef.current = null;
      startedRef.current = false;
      set(outcome);
    });
  }, [set]);

  useEffect(() => {
    if (!ready || loading || value !== null || startedRef.current) return;
    // One frame after the scene renders: the anchors are mounted by then, and
    // driver.js measures the highlight box from real layout.
    const timer = window.setTimeout(start, 400);
    return () => window.clearTimeout(timer);
  }, [ready, loading, value, start]);

  // Leaving the builder mid-tour must take the overlay with it.
  useEffect(() => () => stopRef.current?.(), []);

  return { start, neverRun: value === null };
}
